import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {HttpClient, HttpParams} from '@angular/common/http';
import {environment as env} from '../../../../environments/environment';

/** The journey key the review dialog sends back for a journey the user unticked. */
export interface PublishJourneyKey {
    rsid: string;
    departureDate: string;
    journeyOrigin: string;
    journeyDestination: string;
    passengerClass: string;
}

/**
 * On-demand publishing of re-opt results to RARS (RLP-4140).
 *
 * Kept separate from ReOptimizationService because the same endpoints will serve
 * the inventory-override publish (RLP-4189), which has nothing to do with re-opt.
 */
@Injectable({
    providedIn: 'root'
})
export class RarsPublishService {

    constructor(private http: HttpClient) {
    }

    /** Re-opt requests that have completed but not yet been sent to RARS. */
    public getPublishableRequests(portfolios: string[]): Observable<any> {
        let params = new HttpParams();
        (portfolios || []).forEach(portfolio => {
            params = params.append('portfolios', portfolio);
        });
        return this.http.get<any>(`${env.apiBaseUrl}/rars-publish/publishable`, {params});
    }

    /** Toolbar state: latest publish for a request, plus any in-flight publish. */
    public getLatest(requestId?: string): Observable<any> {
        const params = requestId ? new HttpParams().set('requestId', requestId) : undefined;
        return this.http.get<any>(`${env.apiBaseUrl}/rars-publish/latest`, {params});
    }

    /** Build the payload and park it for review. */
    public stage(requestId: string): Observable<any> {
        return this.http.post<any>(`${env.apiBaseUrl}/rars-publish/stage`,
            {scopeType: 'reopt', requestId});
    }

    public getJob(publishId: string): Observable<any> {
        return this.http.get<any>(`${env.apiBaseUrl}/rars-publish/${publishId}`);
    }

    /**
     * A page of the staged payload. Server-paged because a re-opt scope can run
     * to tens of thousands of bucket rows.
     */
    public getItems(publishId: string, skip = 0, take = 200, status?: string): Observable<any> {
        let params = new HttpParams().set('skip', String(skip)).set('take', String(take));
        if (status) {
            params = params.set('status', status);
        }
        return this.http.get<any>(`${env.apiBaseUrl}/rars-publish/${publishId}/items`, {params});
    }

    /**
     * A page of the staged payload collapsed to journeys, each with its buckets
     * nested. Paged by journey, so `total` is the journey count.
     */
    public getJourneys(publishId: string, skip = 0, take = 100): Observable<any> {
        const params = new HttpParams().set('skip', String(skip)).set('take', String(take));
        return this.http.get<any>(`${env.apiBaseUrl}/rars-publish/${publishId}/journeys`, {params});
    }

    /**
     * Approve the staged payload and release it to RARS. Journeys the user unticked
     * in the review dialog are held back and recorded as Excluded.
     */
    public confirm(publishId: string, excludedJourneys: PublishJourneyKey[] = []): Observable<any> {
        return this.http.post<any>(`${env.apiBaseUrl}/rars-publish/${publishId}/confirm`,
            {excludedJourneys});
    }

    public cancel(publishId: string): Observable<any> {
        return this.http.post<any>(`${env.apiBaseUrl}/rars-publish/${publishId}/cancel`, {});
    }

    /**
     * Record that the user chose not to send this payload. Ignored by the
     * system afterwards, but kept for RA technicians to review.
     */
    public discard(publishId: string): Observable<any> {
        return this.http.post<any>(`${env.apiBaseUrl}/rars-publish/${publishId}/discard`, {});
    }
}
